import { instrumentRunQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunPublic, PaginatedStudiesResponse } from '@/types';
import { FullLoading } from '@components/index';
import { createFileRoute, redirect } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { STUDIES_QUERY_GC_TIME, STUDIES_QUERY_STALE_TIME } from '../../constants';

// Define allowed modes for extensibility
const modes = ['create', 'draft', 'edit'] as const;

const newInstrumentRunSearchSchema = z.object({
  mode: z.enum(modes).optional().default('create'),
  id: z.string().optional(), // ID of the draft/run to edit
});

export const Route = createFileRoute('/instrument_runs/new')({
  validateSearch: newInstrumentRunSearchSchema,
  // Always fetch fresh data - bypass router cache
  // This ensures SampleManager and form always have the latest backend data
  staleTime: 0,
  beforeLoad: ({ context }) => {
    // Redirect to login if user is not authenticated
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({
        to: '/login',
      });
    }

    // Redirect if no organization is selected
    const selectedOrg = useAppStore.getState()?.selectedOrganization;
    if (!selectedOrg) {
      throw redirect({
        to: '/organizations',
      });
    }
  },

  loaderDeps: ({ search }) => ({
    mode: search.mode,
    id: search.id,
  }),

  loader: async ({ context, deps }) => {
    const selectedOrg = useAppStore.getState()?.selectedOrganization;
    const orgId = selectedOrg?.id;

    if (!orgId) {
      throw redirect({
        to: '/organizations',
      });
    }

    // Prefetch studies data
    try {
      await context.queryClient.prefetchQuery<PaginatedStudiesResponse>({
        queryKey: studyQueryKeys.list(orgId),
        queryFn: async () => {
          return searchStudies(orgId, {
            skip: 0,
            limit: 500,
            sort_by: 'created_at',
            sort_order: 'desc',
          });
        },
        staleTime: STUDIES_QUERY_STALE_TIME,
        gcTime: STUDIES_QUERY_GC_TIME,
      });
    } catch (error) {
      console.error('Failed to prefetch studies in loader:', error);
    }

    // If mode is 'draft' or 'edit', fetch the instrument run data from backend
    let instrumentRunData: InstrumentRunPublic | null = null;

    if ((deps.mode === 'draft' || deps.mode === 'edit') && deps.id) {
      const runId = deps.id; // Type narrowing - we know deps.id is defined here
      try {
        // Use fetchQuery instead of ensureQueryData to force a fresh fetch
        // This bypasses TanStack Query cache and always gets latest from backend
        instrumentRunData = await context.queryClient.fetchQuery<InstrumentRunPublic>({
          queryKey: instrumentRunQueryKeys.detail(orgId, runId),
          queryFn: () => fetchInstrumentRun(orgId, runId),
          staleTime: 0, // Always consider stale
        });

        // If fetched data doesn't match the expected mode, redirect appropriately
        if (instrumentRunData) {
          // If mode is 'edit' but status is 'DRAFT', update mode in URL
          if (deps.mode === 'edit' && instrumentRunData.status === 'DRAFT') {
            throw redirect({
              to: '/instrument_runs/new',
              search: { mode: 'draft', id: runId },
            });
          }

          // If mode is 'draft' but status is not 'DRAFT', redirect to detail view
          if (deps.mode === 'draft' && instrumentRunData.status !== 'DRAFT') {
            throw redirect({
              to: '/instrument_runs/$instrumentRunId',
              params: { instrumentRunId: runId },
            });
          }
        }
      } catch (error) {
        // If we can't fetch the run, redirect to create mode
        console.error('Failed to fetch instrument run:', error);
        throw redirect({
          to: '/instrument_runs/new',
          search: { mode: 'create' },
        });
      }
    }

    return {
      orgId,
      mode: deps.mode,
      instrumentRunData,
    };
  },
  pendingComponent: () => {
    const { t } = useTranslation('runs');

    return <FullLoading addEllipsis label={t('loading')} />;
  },
});
